import { Box, Button, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useRef, useState } from 'react';
import ImageCropDialog from '../../components/ImageCropDialog';
import mobileApplicationReleaseProfileService from '../../services/mobileApplicationReleaseProfileService';

const ACCEPT = 'image/png,image/jpeg,image/webp';
const MAX_BYTES = 10 * 1024 * 1024;

async function fileToBase64(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

type BuildConfigFileValue = {
  id: number;
  fileName?: string;
  previewUrl?: string;
  kind?: number;
};

type ReleaseProfileImageFieldProps = {
  label: string;
  kind: number;
  value?: BuildConfigFileValue | null;
  readonly?: boolean;
  aspect?: number;
  onChange: (fileId: number | null, file: BuildConfigFileValue | null) => void;
};

const ReleaseProfileImageField = ({
  label,
  kind,
  value,
  readonly = false,
  aspect = 1,
  onChange,
}: ReleaseProfileImageFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [cropSource, setCropSource] = useState<File | null>(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handlePick = (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      enqueueSnackbar('Csak képfájl tölthető fel.', { variant: 'error' });
      return;
    }
    if (file.size > MAX_BYTES) {
      enqueueSnackbar('A kép legfeljebb 10 MB lehet.', { variant: 'error' });
      return;
    }
    setCropSource(file);
    setCropOpen(true);
  };

  const uploadCropped = async (file: File) => {
    setUploading(true);
    try {
      const response = await mobileApplicationReleaseProfileService.uploadBuildConfigFile({
        kind,
        fileName: file.name,
        contentBase64: await fileToBase64(file),
      });
      if (response.hasError) {
        enqueueSnackbar(response.errorMessages.join(', '), { variant: 'error' });
        return;
      }
      onChange(response.result.id, response.result);
      enqueueSnackbar('Kép feltöltve.', { variant: 'success' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        {label}
      </Typography>
      {value?.fileName && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          Feltöltve: {value.fileName}
        </Typography>
      )}
      {value?.previewUrl && (
        <Box
          component="img"
          src={value.previewUrl}
          alt={label}
          sx={{
            display: 'block',
            maxWidth: 180,
            maxHeight: 180,
            mb: 1,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}
        />
      )}
      {!readonly && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            hidden
            onChange={(e) => {
              handlePick(e.target.files?.[0] ?? null);
              e.target.value = '';
            }}
          />
          <Button
            variant="outlined"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            sx={{ mr: 1 }}
          >
            {value?.id ? 'Kép cseréje' : 'Kép feltöltése'}
          </Button>
          {value?.id && (
            <Button
              variant="text"
              color="secondary"
              disabled={uploading}
              onClick={() => onChange(null, null)}
            >
              Eltávolítás
            </Button>
          )}
        </>
      )}
      <ImageCropDialog
        open={cropOpen}
        sourceFile={cropSource}
        aspect={aspect}
        title={label}
        onClose={() => {
          setCropOpen(false);
          setCropSource(null);
        }}
        onConfirm={(file) => void uploadCropped(file)}
      />
    </Box>
  );
};

export default ReleaseProfileImageField;
